import React, { useEffect, useState, useMemo } from "react";
import { Select, Modal } from "antd";
import { CheckCircleFilled, WarningOutlined, CaretRightOutlined, CaretDownOutlined, PauseCircleFilled, CalendarOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { GetTasksByUnitTreeApi } from "../../api/tasks/GetTasksByUnitTreeApi";
import GetPositionsPerformanceApi from "../../api/individuals/GetPositionsPerformanceApi";
import CompleteTaskApi from "../../api/tasks/CompleteTaskApi";
import { openNotificationSuccess, openNotificationError } from "../../helpers/NotificationHelpers";
import PageLoader from "../sub/PageLoader";
import moment from "moment";

const { Option } = Select;

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function getQuarter(month) {
  // Sri Lankan fiscal year: Q1=Apr-Jun, Q2=Jul-Sep, Q3=Oct-Dec, Q4=Jan-Mar
  if (month >= 3 && month <= 5) return { label: "Q1", months: [3, 4, 5] };
  if (month >= 6 && month <= 8) return { label: "Q2", months: [6, 7, 8] };
  if (month >= 9 && month <= 11) return { label: "Q3", months: [9, 10, 11] };
  return { label: "Q4", months: [0, 1, 2] };
}

function getWeekRange(month, year) {
  const start = moment().year(year).month(month).startOf("month").isoWeek();
  const end = moment().year(year).month(month).endOf("month").isoWeek();
  return `W${start}–W${end}`;
}

function getMonthAccentColor(percentage) {
  if (percentage === 0) return "#f87171";
  if (percentage >= 80) return "#22c55e";
  return "#f59e0b";
}

function getMonthBorderColor(percentage) {
  if (percentage === 0) return "#fecaca";
  if (percentage >= 80) return "#86efac";
  return "#fde68a";
}

export default function MonthlyViewSplitPlans({ drillDown, onClearDrillDown }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [quarter, setQuarter] = useState(null);
  const [selectedYear, setSelectedYear] = useState(drillDown?.year || moment().year());
  const [selectedMonth, setSelectedMonth] = useState(drillDown?.monthIdx !== undefined ? drillDown.monthIdx : null);
  const [expandedPlans, setExpandedPlans] = useState({});
  const [positions, setPositions] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const positionState = useSelector((state) => state.positionState.value);

  useEffect(() => {
    const currentQ = getQuarter(moment().month());
    setQuarter(currentQ);
  }, []);

  // Apply drillDown when it arrives from heatmap
  useEffect(() => {
    if (drillDown) {
      setSelectedYear(drillDown.year || moment().year());
      if (drillDown.monthIdx !== undefined && drillDown.monthIdx !== null) {
        setSelectedMonth(drillDown.monthIdx);
      }
      if (drillDown.positionIds?.length) {
        // Find userId that owns the first positionId
        const matchedUser = positions.find(
          (p) => drillDown.positionIds.includes(p.id)
        );
        if (matchedUser) {
          const userId = matchedUser.users?.id || matchedUser.userId;
          if (userId) setSelectedUserId(userId);
        }
      }
    }
  }, [drillDown]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (perfCycleState?.id && positionState?.id) {
      fetchPositions();
      fetchAllTasks();
    }
  }, [positionState, perfCycleState]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchAllTasks() {
    setLoading(true);
    const response = await GetTasksByUnitTreeApi(perfCycleState.id, positionState.id);
    if (response.success) setTasks(response.data || []);
    setLoading(false);
  }

  async function fetchPositions() {
    setPositionsLoading(true);
    const response = await GetPositionsPerformanceApi(perfCycleState.id, 1, positionState.id, 100);
    if (response.message === "success") setPositions(response.data?.positions || []);
    setPositionsLoading(false);
  }

  async function handleCompleteTask(taskId) {
    const res = await CompleteTaskApi(taskId);
    if (res.success) {
      openNotificationSuccess("bottomRight", "Task completed");
      fetchAllTasks();
    } else {
      openNotificationError("bottomRight", "Couldn't complete task");
    }
  }

  // Deduplicate positions by userId — each user gets one entry with ALL their position IDs
  // Only include users with a name who have at least one task in the loaded task list
  const userOptions = useMemo(() => {
    const activePositionIds = new Set();
    tasks.forEach((t) => {
      if (t.assignedTo) activePositionIds.add(t.assignedTo);
      if (t.plan?.assignedTo) activePositionIds.add(t.plan.assignedTo);
    });

    const map = new Map();
    positions.forEach((pos) => {
      const userId = pos.users?.id || pos.userId;
      if (!userId) return;
      const name = pos.users
        ? `${pos.users.firstName || ""} ${pos.users.lastName || ""}`.trim()
        : "";
      if (!name) return;
      if (!map.has(userId)) {
        map.set(userId, { userId, name, imageUrl: pos.users?.imageUrl || null, positionIds: [] });
      }
      map.get(userId).positionIds.push(pos.id);
    });

    return [...map.values()]
      .filter((u) => u.positionIds.some((pid) => activePositionIds.has(pid)))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [positions, tasks]);

  const selectedPositionIds = useMemo(() => {
    if (!selectedUserId) return null;
    return userOptions.find((u) => u.userId === selectedUserId)?.positionIds || null;
  }, [selectedUserId, userOptions]);

  // Filter tasks by selected user (all their position IDs)
  const filteredTasks = useMemo(() => {
    if (selectedPositionIds) {
      return tasks.filter((task) =>
        selectedPositionIds.includes(task.assignedTo) ||
        selectedPositionIds.includes(task.plan?.assignedTo)
      );
    }
    return tasks;
  }, [tasks, selectedPositionIds]);

  const monthlyData = useMemo(() => {
    const activeMonths = selectedMonth !== null ? [selectedMonth] : (quarter?.months || []);
    return activeMonths.map((monthIdx) => {
      const monthTasks = filteredTasks.filter((task) => {
        if (!task.taskEndDate) return false;
        const taskDate = moment(task.taskEndDate);
        return taskDate.month() === monthIdx && taskDate.year() === selectedYear;
      });
      const completed = monthTasks.filter((t) => t.completionStatus === true).length;
      const percentage = monthTasks.length > 0 ? Math.round((completed / monthTasks.length) * 100) : 0;
      return { month: monthIdx, name: MONTH_NAMES[monthIdx], year: selectedYear, weekRange: getWeekRange(monthIdx, selectedYear), tasks: monthTasks, total: monthTasks.length, completed, percentage };
    });
  }, [filteredTasks, selectedMonth, quarter, selectedYear]);

  const overallTarget = useMemo(() => {
    const totalTasks = monthlyData.reduce((sum, m) => sum + m.total, 0);
    const totalCompleted = monthlyData.reduce((sum, m) => sum + m.completed, 0);
    return totalTasks > 0 ? Math.round((totalCompleted / totalTasks) * 100) : 0;
  }, [monthlyData]);

  const yearOptions = useMemo(() => {
    const current = moment().year();
    return [current - 1, current, current + 1];
  }, []);

  if (loading) return <PageLoader />;

  return (
    <div style={{ maxWidth: 1200 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", margin: 0 }}>Monthly View</h2>
            {drillDown && (
              <div
                onClick={onClearDrillDown}
                style={{ fontSize: 11, padding: "3px 10px", borderRadius: 10, backgroundColor: "#f1f5f9", border: "1px solid #e2e8f0", color: "#64748b", cursor: "pointer", fontWeight: 600 }}
              >
                × Clear filter
              </div>
            )}
          </div>
          <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 4 }}>
            {drillDown ? (
              <span style={{ color: "#ea580c", fontWeight: 600 }}>Filtered: {MONTH_NAMES[drillDown.monthIdx]} {drillDown.year}</span>
            ) : (
              <>{userOptions.length > 1 ? "All tasks" : (positionState?.users?.firstName || "User")} · Each month feeds into weekly tasks</>
            )}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <Select value={selectedYear} onChange={(val) => setSelectedYear(val)} size="small" style={{ width: 80 }}>
            {yearOptions.map((y) => <Option key={y} value={y}>{y}</Option>)}
          </Select>
          <Select value={selectedMonth === null ? "quarter" : selectedMonth} onChange={(val) => setSelectedMonth(val === "quarter" ? null : val)} size="small" style={{ width: 120 }}>
            <Option value="quarter">By Quarter</Option>
            {MONTH_NAMES.map((name, idx) => <Option key={idx} value={idx}>{name}</Option>)}
          </Select>
          {selectedMonth === null && (
            <Select value={quarter?.label} onChange={(val) => { const quarters = { Q1: { label: "Q1", months: [3, 4, 5] }, Q2: { label: "Q2", months: [6, 7, 8] }, Q3: { label: "Q3", months: [9, 10, 11] }, Q4: { label: "Q4", months: [0, 1, 2] } }; setQuarter(quarters[val]); }} size="small" style={{ width: 70 }}>
              <Option value="Q1">Q1</Option><Option value="Q2">Q2</Option><Option value="Q3">Q3</Option><Option value="Q4">Q4</Option>
            </Select>
          )}
          {userOptions.length > 1 && (
            <Select
              value={selectedUserId}
              onChange={(val) => setSelectedUserId(val ?? null)}
              style={{ width: 180 }} size="small"
              showSearch allowClear placeholder="All users"
              optionFilterProp="label"
              loading={positionsLoading}
            >
              {userOptions.map((u) => (
                <Option key={u.userId} value={u.userId} label={u.name}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {u.imageUrl ? (
                      <img src={u.imageUrl} alt={u.name} style={{ width: 22, height: 22, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                    ) : (
                      <div style={{ width: 22, height: 22, borderRadius: "50%", backgroundColor: "#0f172a", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                        {u.name.split(" ").filter(Boolean).map(n => n[0].toUpperCase()).slice(0, 2).join("")}
                      </div>
                    )}
                    <span style={{ fontSize: 12 }}>{u.name}</span>
                  </div>
                </Option>
              ))}
            </Select>
          )}
          {/* Quarterly progress pill */}
          <div style={{ padding: "5px 14px", borderRadius: 6, backgroundColor: "#059669", color: "#fff", fontSize: 12, fontWeight: 700 }}>
            {selectedMonth !== null ? MONTH_NAMES[selectedMonth] : quarter?.label} {selectedYear}: {overallTarget}%
          </div>
        </div>
      </div>

      {/* Status Indicators */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, padding: "10px 16px", backgroundColor: "#fafbfc", borderRadius: 8, border: "1px solid #e2e8f0" }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "#334155", textTransform: "uppercase", letterSpacing: "0.5px" }}>Task Status Indicators & Highlights:</span>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 12, backgroundColor: "#059669", color: "#fff", fontSize: 11, fontWeight: 700 }}>
          <CheckCircleFilled style={{ fontSize: 10 }} /> 100% Completed
        </div>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 12, backgroundColor: "#fbbf24", color: "#78350f", fontSize: 11, fontWeight: 700 }}>
          <PauseCircleFilled style={{ fontSize: 10 }} /> On Hold Task
        </div>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 12, backgroundColor: "#f1f5f9", color: "#475569", fontSize: 11, fontWeight: 600, border: "1px solid #e2e8f0" }}>
          ○ In Progress
        </div>
      </div>

      {/* Monthly Columns Grid */}
      {filteredTasks.length === 0 ? (
        <div className="flex justify-center items-center" style={{ minHeight: 250 }}>
          <div className="text-center">
            <div className="text-3xl text-gray-400"><WarningOutlined /></div>
            <div className="text-lg pri-font text-gray-400 mt-2">No tasks found</div>
            <div className="text-gray-400 text-sm">No tasks available for this performance cycle</div>
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${monthlyData.length}, minmax(0, 380px))`, gap: 16, alignItems: "flex-start" }}>
          {monthlyData.map((month) => {
            const accentColor = getMonthAccentColor(month.percentage);
            const borderColor = getMonthBorderColor(month.percentage);

            // Group tasks by parent plan → split plan hierarchy
            const parentPlanMap = new Map();
            month.tasks.forEach(task => {
              const planId = task.planId || "unknown";
              const planTitle = task.plan?.title || "Unassigned Plan";
              const splitFrom = task.plan?.splitFrom || null;
              
              // Use parentPlanTitle from backend (cascade/parent plan above the split plan)
              const parentKey = splitFrom || planId;
              const parentTitle = task.parentPlanTitle || (splitFrom ? "Cascade Plan" : planTitle);
              
              if (!parentPlanMap.has(parentKey)) {
                parentPlanMap.set(parentKey, { title: parentTitle, splitPlans: new Map() });
              }
              
              const parent = parentPlanMap.get(parentKey);
              if (!parent.splitPlans.has(planId)) {
                parent.splitPlans.set(planId, { title: planTitle, tasks: [] });
              }
              parent.splitPlans.get(planId).tasks.push(task);
            });

            // Sort tasks within each split plan: on hold → pending → completed
            const sortTasks = (tasks) => {
              return [...tasks].sort((a, b) => {
                const getOrder = (t) => {
                  if (t.onHold) return 0;
                  if (!t.completionStatus) return 1;
                  return 2;
                };
                return getOrder(a) - getOrder(b);
              });
            };

            return (
              <div key={month.month} style={{ border: `1px solid ${accentColor}`, borderRadius: 12, backgroundColor: "#fff", overflow: "hidden", position: "relative" }}>
                {/* Month Header */}
                <div style={{ padding: "14px 18px 10px", borderBottom: "1px solid #f1f5f9" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: 17, fontWeight: 800, color: "#0f172a" }}>{month.name} {month.year}</div>
                      <div style={{ fontSize: 11, color: "#94a3b8", fontFamily: "monospace" }}>{month.weekRange}</div>
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: accentColor }}>{month.percentage}%</div>
                  </div>
                </div>

                {/* Plan groups & tasks */}
                <div style={{ padding: "12px 14px 16px" }}>
                  {month.tasks.length === 0 ? (
                    <div style={{ color: "#94a3b8", fontSize: 12, textAlign: "center", padding: "20px 0" }}>No tasks this month</div>
                  ) : (() => {
                    // Sort parent groups: ones where ALL split plans are 100% go to the bottom
                    const sortedParentGroups = Array.from(parentPlanMap.entries()).sort(([, aParent], [, bParent]) => {
                      const allA = Array.from(aParent.splitPlans.values()).flatMap(g => g.tasks);
                      const allB = Array.from(bParent.splitPlans.values()).flatMap(g => g.tasks);
                      const aPct = allA.length > 0 ? Math.round((allA.filter(t => t.completionStatus).length / allA.length) * 100) : 0;
                      const bPct = allB.length > 0 ? Math.round((allB.filter(t => t.completionStatus).length / allB.length) * 100) : 0;
                      if (aPct === 100 && bPct !== 100) return 1;
                      if (bPct === 100 && aPct !== 100) return -1;
                      return aPct - bPct;
                    });
                    return sortedParentGroups.map(([parentId, parentGroup]) => {
                      // Also sort split plans within each parent: 100% go to bottom
                      const sortedSplitPlans = Array.from(parentGroup.splitPlans.entries()).sort(([, aGroup], [, bGroup]) => {
                        const aPct = aGroup.tasks.length > 0 ? Math.round((aGroup.tasks.filter(t => t.completionStatus).length / aGroup.tasks.length) * 100) : 0;
                        const bPct = bGroup.tasks.length > 0 ? Math.round((bGroup.tasks.filter(t => t.completionStatus).length / bGroup.tasks.length) * 100) : 0;
                        if (aPct === 100 && bPct !== 100) return 1;
                        if (bPct === 100 && aPct !== 100) return -1;
                        return aPct - bPct;
                      });
                      return (
                        <div key={parentId} style={{ marginBottom: 14 }}>
                          {parentGroup.splitPlans.size > 0 && Array.from(parentGroup.splitPlans.values())[0]?.tasks[0]?.plan?.splitFrom && (
                            <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 6, paddingLeft: 4 }}>
                              {parentGroup.title}
                            </div>
                          )}
                          {parentGroup.splitPlans.size > 0 && sortedSplitPlans.map(([planId, group]) => {
                            const key = `${planId}-${month.month}`;
                            const isExpanded = expandedPlans[key] !== false;
                            const planCompleted = group.tasks.filter(t => t.completionStatus).length;
                            const planPct = group.tasks.length > 0 ? Math.round((planCompleted / group.tasks.length) * 100) : 0;
                            const planBarColor = planPct >= 80 ? "#22c55e" : planPct >= 40 ? "#f59e0b" : "#ef4444";
                            return (
                              <div key={planId} style={{ marginBottom: 12 }}>
                                <div
                                  onClick={() => setExpandedPlans(prev => ({ ...prev, [key]: !isExpanded }))}
                                  style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", padding: "6px 8px", borderRadius: 6, backgroundColor: "#f8fafc", marginBottom: 6, userSelect: "none" }}
                                >
                                  <span style={{ color: "#64748b", fontSize: 11 }}>{isExpanded ? <CaretDownOutlined /> : <CaretRightOutlined />}</span>
                                  <span style={{ fontSize: 12, fontWeight: 700, color: "#1e293b", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{group.title}</span>
                                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                                    <div style={{ width: 40, height: 4, borderRadius: 2, backgroundColor: "#e2e8f0", overflow: "hidden" }}>
                                      <div style={{ width: `${planPct}%`, height: "100%", backgroundColor: planBarColor, borderRadius: 2 }} />
                                    </div>
                                    <span style={{ fontSize: 11, fontWeight: 700, color: planBarColor }}>{planPct}%</span>
                                  </div>
                                </div>
                                {isExpanded && sortTasks(group.tasks).map(task => {
                                  const assignedName = task.positions?.users ? `${task.positions.users.firstName || ""} ${task.positions.users.lastName || ""}`.trim() : null;
                                  const isOnHold = task.onHold;
                                  return (
                                    <div
                                      key={task.id}
                                      style={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: 8,
                                        marginBottom: 8,
                                        paddingLeft: 12,
                                        padding: isOnHold ? "8px 10px" : "4px 10px",
                                        borderRadius: isOnHold ? 6 : 0,
                                        backgroundColor: isOnHold ? "#fef3c7" : "transparent",
                                        border: isOnHold ? "1px solid #fde68a" : "none",
                                      }}
                                    >
                                      <div style={{ marginTop: 2, flexShrink: 0 }}>
                                        {task.completionStatus ? (
                                          <CheckCircleFilled style={{ color: "#22c55e", fontSize: 14 }} />
                                        ) : isOnHold ? (
                                          <PauseCircleFilled style={{ color: "#d97706", fontSize: 14 }} />
                                        ) : (
                                          <div onClick={() => { Modal.confirm({ title: "Mark task as completed?", content: `Are you sure you want to mark "${task.name}" as completed?`, okText: "Mark completed", cancelText: "Cancel", onOk: () => handleCompleteTask(task.id), }); }} style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid #cbd5e1", cursor: "pointer" }} title="Click to complete" />
                                        )}
                                      </div>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                          <span style={{ fontSize: 12, color: task.completionStatus ? "#1e293b" : "#475569", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {task.name}
                                          </span>
                                          {isOnHold && (
                                            <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, backgroundColor: "#92400e", color: "#fff", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", flexShrink: 0 }}>On Hold</span>
                                          )}
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 3, fontSize: 10, color: "#94a3b8" }}>
                                          {task.taskEndDate && (
                                            <span style={{ fontFamily: "monospace" }}>
                                              <CalendarOutlined style={{ marginRight: 3 }} /> Target: {moment(task.taskEndDate).format("DD  MMM  YYYY")}
                                            </span>
                                          )}
                                          {assignedName && (() => {
                                            const imageUrl = task.positions?.users?.imageUrl || null;
                                            const initials = assignedName.split(" ").filter(Boolean).map(n => n[0].toUpperCase()).slice(0, 2).join("");
                                            return (
                                              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                                {imageUrl ? (
                                                  <img src={imageUrl} alt={assignedName} style={{ width: 16, height: 16, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                                                ) : (
                                                  <div style={{ width: 16, height: 16, borderRadius: "50%", backgroundColor: "#0f172a", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 7, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                                                    {initials}
                                                  </div>
                                                )}
                                                {assignedName}
                                              </span>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
